import { ChevronLeft, ChevronRight } from '@project-lary/react-material-symbols';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import type * as React from 'react';
import { type CaptionProps, DayPicker, useNavigation } from 'react-day-picker';
import { cn } from '../utils/tailwind-merge';
import { Button, ButtonVariant, buttonVariants } from './button';

function CustomCaption(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  return (
    <div className="flex justify-between">
      <Button
        variant={ButtonVariant.ghost_neutral}
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        Icon={ChevronLeft}
        classNames={{ icon: 'mr-0' }}
      />
      {format(props.displayMonth, 'MMM yyy', { locale: de })}
      <Button
        variant={ButtonVariant.ghost_neutral}
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        Icon={ChevronRight}
        classNames={{ icon: 'mr-0' }}
      />
    </div>
  );
}

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, fixedWeeks = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-0', className)}
      locale={de}
      classNames={{
        months: 'flex flex-col justify-center sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 text-text',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-text',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: ButtonVariant.ghost_neutral }),
          'ml-2 size-8 p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex ',
        head_cell: 'flex justify-center rounded-md w-9 p-0 font-medium text-xs',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 border-none text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-white [&:has([aria-selected])]:bg-neutral-3 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          'font-regular flex size-9 items-center justify-center p-0 text-center text-sm antialiased transition-colors hover:rounded-md hover:bg-neutral-alpha-4 disabled:pointer-events-none disabled:bg-neutral-alpha-3 disabled:text-neutral-alpha-10 disabled:opacity-50 aria-selected:opacity-100',
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-neutral-alpha-12 text-white rounded-md hover:bg-neutral-alpha-12 hover:text-white focus:bg-neutral-alpha-12 focus:text-white',
        day_today: 'bg-neutral-alpha-3',
        day_outside: 'day-outside text-neutral-12 opacity-50 aria-selected:text-white ',
        day_disabled: 'text-neutral-alpha-8',
        day_range_middle: 'aria-selected:bg-neutral-3 aria-selected:text-text',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Caption: CustomCaption,
      }}
      modifiers={props.modifiers}
      fixedWeeks={fixedWeeks}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
